import React, { useEffect, useRef, useState } from "react";
import { Alert, Input, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { usePutExternalNameMutation } from "../../../redux/externals/externalsAPI";
import GETJwtToken from "../../../redux/authentication/authentication";
import type { ExternalDetail } from "../../../interfaces/External.interface";
import apiClientErrorMessage from "./apiClientErrors";

interface ApiClientRenameModalProps {
  /** The API client being renamed, or null when the modal is closed. */
  client: ExternalDetail | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Changes an API client's name; its role, grants and credentials are kept. */
function ApiClientRenameModal({ client, onClose, onSaved }: ApiClientRenameModalProps) {
  const [t] = useTranslation("global");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [putName] = usePutExternalNameMutation();
  // Set synchronously so a second Enter during the token wait cannot send twice.
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(client?.name ?? "");
    setError("");
  }, [client?.id]);

  const submit = async () => {
    if (savingRef.current || client === null) return;
    const trimmed = name.trim();
    if (trimmed === "") {
      setError(t("apiClients.nameRequired"));
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      const token = await GETJwtToken();
      await putName({ token, id: client.id, name: trimmed }).unwrap();
      onSaved();
    } catch (err) {
      setError(apiClientErrorMessage(err, t("apiClients.genericError")));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      open={client !== null}
      title={t("apiClients.renameTitle", { name: client?.name ?? "" })}
      okText={t("apiClients.save")}
      cancelText={t("apiClients.cancel")}
      onOk={submit}
      onCancel={saving ? undefined : onClose}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      confirmLoading={saving}
      okButtonProps={{ "data-testid": "api-client-rename-submit" } as Record<string, unknown>}
      cancelButtonProps={
        { "data-testid": "api-client-rename-cancel", disabled: saving } as Record<string, unknown>
      }
      destroyOnClose
    >
      <div data-testid="api-client-rename-modal">
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            className="mb-3"
            data-testid="api-client-rename-error"
          />
        )}
        <span className="drawer__text">{t("apiClients.name")}</span>
        <Input
          data-testid="api-client-rename-name"
          value={name}
          maxLength={255}
          onChange={(event) => setName(event.target.value)}
          onPressEnter={submit}
          style={{ marginTop: 6 }}
        />
      </div>
    </Modal>
  );
}

export default ApiClientRenameModal;
